import React from 'react';
import { ArrowLeftIcon } from 'components/common/icons';
import { ContactPersonAddEdit } from 'components/common/contactPerson/addEdit';
import { AddSupplierModal, Button, Col, Divider, Image, Row, Typography } from 'components';
import { useAttachments, useEmailContent, useRequestPrice, useSuppliers } from './hooks';
import { AttachmentsSection, EmailSection, SupplierSection } from './components';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';
import styles from './styles.module.scss';

export const PurchaseOrderRequestPrice: React.FC = () => {
  const { form, brandIds, currentUser, isSending, handleSendRequest, handleCancel, generateItemsTable } =
    useRequestPrice();

  const {
    suppliers,
    supplierOptions,
    filteredSuppliers,
    contactPersons,
    loadingContactPersons,
    selectedSupplierId,
    disabledSuppActions,
    openSupplierModal,
    setOpenSupplierModal,
    openContactPersonModal,
    setOpenContactPersonModal,
    handleSupplierChange,
    handleContactPersonChange,
    handleAddSupplier,
    handleRemoveSupplier,
    resetForm,
  } = useSuppliers({
    form,
    brandIds,
    onSupplierAdded: async (supplierId, contactPersonId, languageId) => {
      await handleSupplierAdded(languageId);
    },
  });

  const {
    selectedLanguage,
    languageEmailBody,
    languageEmailSubject,
    handleLanguageChange,
    handleBodyChange,
    handleSubjectChange,
    handleSupplierAdded,
    handleSupplierRemoved,
    uniqueLanguages,
  } = useEmailContent({
    suppliers,
    generateItemsTable,
  });

  const {
    attachmentType,
    fileList,
    attachments,
    error,
    disabledActions,
    previewImage,
    isPreviewVisible,
    attachmentTypes,
    setAttachmentType,
    setFileList,
    setIsPreviewVisible,
    handleAddAttachment,
    handleDeleteAttachment,
    handleFileSizeValidation,
    handleCancelAttachment,
  } = useAttachments();

  const handleRemoveSupplierWithEmailCleanup = (index: number) => {
    const updatedSuppliers = handleRemoveSupplier(index);
    handleSupplierRemoved(updatedSuppliers);
    return updatedSuppliers;
  };

  return (
    <div>
      <div style={{ paddingLeft: 24 }}>
        <Button title={'Click to go back'} onClick={() => history.back()} type={'link'} icon={<ArrowLeftIcon />} />
      </div>
      <div className={styles.container}>
        <Row justify={'end'} gutter={[0, 32]}>
          <Col span={24}>
            <Row gutter={[0, 24]} justify={'center'}>
              <Col span={24}>
                <Typography.Title className={styles.add_edit_title} level={2}>
                  Request Price
                </Typography.Title>
              </Col>
              <Col span={24} className={styles.form_container}>
                <Row gutter={[0, 24]}>
                  {/* Supplier Section */}
                  <Col span={24}>
                    <SupplierSection
                      form={form}
                      suppliers={suppliers}
                      supplierOptions={supplierOptions}
                      filteredSuppliers={filteredSuppliers}
                      contactPersons={contactPersons}
                      loadingContactPersons={loadingContactPersons}
                      selectedSupplierId={selectedSupplierId}
                      disabledSuppActions={disabledSuppActions}
                      handleSupplierChange={handleSupplierChange}
                      handleContactPersonChange={handleContactPersonChange}
                      handleAddSupplier={handleAddSupplier}
                      handleRemoveSupplier={handleRemoveSupplierWithEmailCleanup}
                      resetForm={resetForm}
                      setOpenSupplierModal={setOpenSupplierModal}
                      setOpenContactPersonModal={setOpenContactPersonModal}
                    />
                  </Col>

                  {/* Email Section */}
                  <Col span={24}>
                    <EmailSection
                      suppliers={suppliers}
                      selectedLanguage={selectedLanguage}
                      currentUserEmail={currentUser.email}
                      languageEmailSubject={languageEmailSubject}
                      languageEmailBody={languageEmailBody}
                      handleLanguageChange={handleLanguageChange}
                      handleSubjectChange={handleSubjectChange}
                      handleBodyChange={handleBodyChange}
                      uniqueLanguages={uniqueLanguages}
                    />
                  </Col>

                  {/* Attachments Section */}
                  <Col span={24}>
                    <AttachmentsSection
                      attachments={attachments}
                      attachmentType={attachmentType}
                      fileList={fileList}
                      error={error}
                      disabledActions={disabledActions}
                      attachmentTypes={attachmentTypes}
                      setAttachmentType={setAttachmentType}
                      setFileList={setFileList}
                      handleAddAttachment={handleAddAttachment}
                      handleDeleteAttachment={handleDeleteAttachment}
                      handleFileSizeValidation={handleFileSizeValidation}
                      handleCancelAttachment={handleCancelAttachment}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col offset={1} span={23}>
            <Divider style={{ marginBottom: 32 }} className={styles.divider} />
          </Col>
          <Col flex={'328px'}>
            <Row justify={'end'} wrap={false} className="gap-8">
              <Button block size="large" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                block
                size="large"
                type="primary"
                loading={isSending}
                onClick={() => {
                  handleSendRequest(suppliers, languageEmailBody, languageEmailSubject, attachments);
                }}
              >
                Send Request
              </Button>
            </Row>
          </Col>
        </Row>
        {/* Image Preview Modal (hidden image trigger) */}
        {previewImage && (
          <Image
            src={previewImage}
            style={{ display: 'none' }}
            preview={{
              visible: isPreviewVisible,
              onVisibleChange: (visible) => setIsPreviewVisible(visible),
            }}
          />
        )}
        <AddSupplierModal onModalStateChange={() => setOpenSupplierModal((os) => !os)} modalState={openSupplierModal} />
        <ContactPersonAddEdit
          entityName={'SUPPLIER'}
          modalState={!!openContactPersonModal}
          clientId={openContactPersonModal}
          onModalStateChange={() => setOpenContactPersonModal('')}
        />
      </div>
    </div>
  );
};
